<script lang="ts">
  import Header from "./Header.svelte";
  import ProductRow from "./ProductRow.svelte";
  import DealError from "./DealError.svelte";
  import AboutModal from "@retailer/svelte/DealTiers/AboutModal.svelte";
  import ProgressBar from "./ProgressBar.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { DealTierDescriptionEntity } from "@shared/services/event_tracking/entities";

  import type { DealResponse, HappyHourDealTier } from "@shared/types/Deals";

  export let data: DealResponse;

  const { deal, distributor } = data;
  const { trackEvent } = EventTracking;
  const isRip = deal.isRip;
  let selectedTier = deal.tiers[0];

  let showAboutModal = false;
  const openModal = () => {
    showAboutModal = true;
  };

  const closeModal = () => {
    showAboutModal = false;
  };

  const getStartDate = (tier: HappyHourDealTier): string | null => {
    return tier?.products[0]?.discount?.startDateTime;
  };

  const getEndDate = (tier: HappyHourDealTier): string | null => {
    return tier?.products[0]?.discount?.endDateTime;
  };

  const getNextTier = (tier: HappyHourDealTier): HappyHourDealTier | null => {
    const index = deal.tiers.indexOf(tier);
    return deal.tiers[index + 1] || null;
  };

  for (const tier of deal.tiers) {
    const entities = new DealTierDescriptionEntity({
      product_id: tier.products[0].product_id,
      deal_tier_id: null,
      distributor_id: distributor.id,
      product_group_id: deal.productGroupId,
      sku: tier.products[0].sku,
    });
    trackEvent("begin_to_render", [entities]);
  }

  $: startDate = getStartDate(selectedTier);
  $: endDate = getEndDate(selectedTier);
  $: dealActive = Date.parse(endDate) > Date.now();
  $: displayedProducts = selectedTier.products.filter(
    product => product.frontlinePrice && product.product_line_id,
  );
  $: productCountText = pluralize("Product", displayedProducts.length, true);
  $: nextDealTier = getNextTier(selectedTier);
</script>

<style>
  .fizz-button-group > button:focus {
    outline: none;
    box-shadow: none;
  }
</style>

<div class="fizz-styles">
  <AboutModal bind:showAboutModal {closeModal} {isRip} />
  <div class="fizz-layout-header-main">
    <Header {openModal} {distributor} {deal} {startDate} {endDate} />

    <div data-grid-area="section" class="fizz-margin-auto" data-testid="tier-button-group">
      <div class="fizz-scroll-horizontal" style="max-width: calc(100vw - 2rem);">
        <span class="fizz-button-group">
          {#each deal.tiers as tier}
            <button
              id={"deal_tier-" + tier.tier}
              class="fizz-button-small"
              class:fizz-is-active={tier === selectedTier}
              on:click={() => (selectedTier = tier)}
            >
              {tier.quantityLabel}<br />
              {tier.maxDiscountLabel}
            </button>
          {/each}
        </span>
      </div>
    </div>

    <main data-grid-area="main">
      {#if selectedTier}
        <section
          class="fizz-space-between fizz-text-small fizz-surface fizz-inset-squish-24 fizz-border-bottom fizz-text-medium"
        >
          <p>{productCountText}</p>
          <ProgressBar distributorId={distributor.id} tier={selectedTier} {nextDealTier} {isRip} />
        </section>
        <table class="fizz-table fizz-text-medium">
          <thead>
            <tr>
              <th>Product</th>
              <th>List Price</th>
              {#if !isRip}
                <th>Deal Price</th>
              {/if}
              {#if dealActive}
                <th>Items In Cart</th>
              {/if}
            </tr>
          </thead>
          <tbody>
            {#each displayedProducts as product}
              <ProductRow
                {product}
                distributorId={distributor.id}
                productGroupId={deal.productGroupId}
                {dealActive}
                {isRip}
              />
            {/each}
          </tbody>
        </table>
      {:else}
        <DealError />
      {/if}
    </main>
  </div>
</div>
